import React, { useEffect, useMemo } from 'react';
import { Popover } from 'freemium-ui';
import i18next from 'i18next';
import { startCase } from 'lodash';
import { DeltaStatic, RangeStatic, Sources, StringMap } from 'quill';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill, UnprivilegedEditor } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PlaceholderConfig, PlaceholderType } from '../../types';
import { useOnClickOutside } from '../../../hooks';
import {
  convertQuillHTMLToText,
  convertStringWithTagsToQuillHTML,
  handleKeyboardEnter,
} from '../../utils';
import PlaceholderMenu from '../PlaceholderMenu';
import { PopupLink, QuillContainer } from './style';

const PLACEHOLDER_BLOT_NAME = 'placeholderBlot';

interface QuillEditorProps {
  id?: string;
  className?: string;
  label?: string;
  placeholder?: string;
  errorText?: string;
  popupText?: string;
  showActions?: boolean;
  open?: boolean;
  isRequired?: boolean;
  isErrored?: boolean;
  disabled?: boolean;
  popoupOHover?: boolean;
  value: string;
  getValueAs: 'STRING' | 'HTML' | 'CONTENT_STRING' | 'CONTENT_HTML';
  placeholders: PlaceholderType[];
  placeholderConfig?: PlaceholderConfig;
  onChange?: (quillValue: string) => void;
  maxCharLimit?: number;
}

type EditorRef = Omit<InstanceType<typeof ReactQuill>, 'getEditor'> & {
  getEditor(): InstanceType<typeof Quill> & {
    container: HTMLDivElement;
  };
};

const AlignStyle = Quill.import('attributors/style/align');
const BackgroundStyle = Quill.import('attributors/style/background');
const ColorStyle = Quill.import('attributors/style/color');
const DirectionStyle = Quill.import('attributors/style/direction');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
const Embed = Quill.import('blots/embed');
const IconsStyle = Quill.import('ui/icons');

class PlaceholderBlot extends Embed {
  static create(data: string) {
    const node = super.create(data);
    node.innerHTML += data;
    const placeholder = data.substr(2, data.length - 4);
    node.dataset.content = i18next.t(
      `contacts.import_contact.default_fields.${placeholder}`,
      startCase(placeholder.split('.').pop()),
    );
    return node;
  }
  static value(domNode: HTMLElement) {
    return domNode.children?.[0]?.textContent || '';
  }
  static formats(domNode: HTMLElement) {
    return domNode.children?.[0]?.textContent || '';
  }
}

//configurations
PlaceholderBlot.blotName = PLACEHOLDER_BLOT_NAME;
PlaceholderBlot.tagName = 'span';
PlaceholderBlot.className = 'placeholder-tag';
IconsStyle.align['left'] = IconsStyle.align['']; // set icon for 'left' option, otherwise it's replaced with 'undefined' text
AlignStyle.whitelist = ['left', 'center', 'right', 'justify'];

//registering
Quill.register(AlignStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(ColorStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);
Quill.register(IconsStyle, true);

//custom registry
Quill.register(PlaceholderBlot, true);

/**
 * Sets accessibility attributes to format buttons in the Quill editor.
 */
const setA11yToFormatButtons = () => {
  document
    .querySelectorAll('.quill.is-open .ql-formats button')
    .forEach((e) => {
      e.setAttribute('tabindex', '0');
      e.setAttribute(
        'aria-label',
        e.getAttribute('value') ||
          e.getAttribute('class')?.replace('ql-', '') ||
          'formatting button',
      );
    });
};

const QuillEditor = (props: QuillEditorProps) => {
  const { t } = useTranslation();
  const {
    id = 'quill-editor',
    placeholders,
    placeholderConfig,
    popupText = `${t('personalize')}`,
    label = '',
    isErrored,
    errorText,
    value,
    showActions = false,
    popoupOHover = true,
    className,
    onChange = () => {},
    placeholder,
    getValueAs,
    disabled,
    isRequired,
    open,
    maxCharLimit,
  } = props;
  const contentEditorRef = React.useRef<EditorRef>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [anchorElMessage, setAnchorElMessage] =
    React.useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  useOnClickOutside(containerRef, () => {
    setTimeout(() => {
      !anchorElMessage && setIsOpen(false);
    }, 200);
  });

  const textCharacterCount = convertQuillHTMLToText(value).length;

  const insertPlaceholder = (ph: string) => {
    const editor =
      contentEditorRef &&
      contentEditorRef.current &&
      contentEditorRef.current.getEditor();
    if (!editor) return;
    const selection = editor.getSelection(true);
    editor?.insertEmbed(selection.index, PLACEHOLDER_BLOT_NAME, ph, 'silent');

    if (placeholderConfig?.isAddSpaceAtEnd) {
      // Add a space after the marker
      editor?.insertText(selection.index + 1, ' ', 'silent');
      // Take the cursor to the end of the inserted PlaceholderTag
      editor?.setSelection(selection.index + 2, 0);
    } else {
      // Take the cursor to the end of the inserted PlaceholderTag
      editor?.setSelection(selection.index + 1, 0);
    }
  };

  const modules = useMemo(() => {
    const modulesToAdd: StringMap = {
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: () => contentEditorRef.current?.getEditor().blur(),
          },
        },
      },
    };

    if (showActions) {
      modulesToAdd.toolbar = [
        ['bold', 'italic', 'underline'],
        [{ list: 'bullet' }],
        [{ align: 'left' }, { align: 'center' }, { align: 'right' }],
        ['link'],
      ];
    }

    return modulesToAdd;
  }, [showActions]);

  const setSelection = (ind: number) => {
    const editor =
      contentEditorRef &&
      contentEditorRef.current &&
      contentEditorRef.current.getEditor();
    if (!editor) return;
    editor?.setSelection(ind, 0);
  };

  const handleFocusAndSelection = (
    selection: RangeStatic,
    _source: Sources,
    editor: UnprivilegedEditor,
  ): void => {
    const editorInstance =
      contentEditorRef &&
      contentEditorRef.current &&
      contentEditorRef.current.getEditor();
    if (!editor) return;
    if (!editorInstance) return;
    const index = selection?.index || 0;
    if (index <= 0) return;
    const baseOffset =
      editorInstance?.root?.ownerDocument?.getSelection?.()?.anchorOffset;

    const text = editor.getText(index, 1);
    const ops = editor.getContents(index).ops;

    if (
      ops?.[0]?.attributes &&
      ops?.[0]?.insert?.[PLACEHOLDER_BLOT_NAME] &&
      text === '' &&
      baseOffset === 0
    ) {
      setSelection(index + 1);
    }
  };

  useEffect(() => {
    if (open && contentEditorRef.current) {
      contentEditorRef.current.getEditor().focus();
      contentEditorRef.current.getEditor().container.click();
      contentEditorRef.current.getEditor().container.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [open]);

  useEffect(() => {
    if (anchorElMessage) {
      setIsOpen(true);
    }
  }, [anchorElMessage]);

  useEffect(() => {
    if (isOpen) {
      setA11yToFormatButtons();
    }
  }, [isOpen]);

  return (
    <QuillContainer
      isErrored={isErrored}
      popoupOHover={popoupOHover}
      showActions={showActions}
      disabled={disabled}
      onClick={() => {
        setIsOpen(true);
      }}
      ref={containerRef}
    >
      <div className="label">
        {label}
        {isRequired ? <span className="lab-req">*</span> : ''}
      </div>
      <ReactQuill
        id={id}
        className={`react-quill-component ${isOpen && 'is-open'} ${
          className || ''
        }`}
        theme="snow"
        value={getValueAs === 'STRING' ? `<p>${value || ''}</p>` : value}
        ref={contentEditorRef}
        readOnly={disabled}
        preserveWhitespace
        modules={modules}
        formats={[
          'bold',
          'italic',
          'underline',
          'list',
          'align',
          'link',
          PLACEHOLDER_BLOT_NAME,
        ]}
        placeholder={placeholder}
        onChangeSelection={handleFocusAndSelection}
        onFocus={handleFocusAndSelection}
        onBlur={() => {
          const el = document.querySelector(
            '.ql-tooltip.ql-editing [data-link]',
          );
          if (!el) {
            return;
          }
          el.setAttribute('placeholder', 'https://example.com');
          el.setAttribute('data-link', 'https://example.com');
        }}
        onChange={(
          val: string,
          _delta: DeltaStatic,
          _source: Sources,
          editor: UnprivilegedEditor,
        ) => {
          switch (getValueAs) {
            case 'HTML':
              onChange(editor.getHTML());
              break;
            case 'STRING':
              onChange(editor.getText().trim());
              break;
            case 'CONTENT_HTML':
              onChange(
                convertStringWithTagsToQuillHTML(
                  editor
                    .getContents()
                    .map((cnt) => {
                      if (typeof cnt.insert === 'string') return cnt.insert;
                      if (
                        typeof cnt?.insert?.[PLACEHOLDER_BLOT_NAME] === 'string'
                      )
                        return cnt.insert[PLACEHOLDER_BLOT_NAME];
                      return '';
                    })
                    .join(''),
                ),
              );
              break;
            case 'CONTENT_STRING':
              onChange(
                editor
                  .getContents()
                  .map((cnt) => {
                    if (typeof cnt.insert === 'string') return cnt.insert;
                    if (
                      typeof cnt?.insert?.[PLACEHOLDER_BLOT_NAME] === 'string'
                    )
                      return cnt.insert[PLACEHOLDER_BLOT_NAME];
                    return '';
                  })
                  .join(''),
              );
              break;
            default:
              onChange(val);
          }
        }}
      />
      {isOpen && maxCharLimit ? (
        <div
          className="char-count"
          style={{
            color:
              textCharacterCount > maxCharLimit
                ? 'var(--color-persimmon-800)'
                : 'var(--base-color)',
          }}
        >
          {maxCharLimit ? `${textCharacterCount} / ${maxCharLimit}` : ''}
        </div>
      ) : null}

      {placeholders.length ? (
        <>
          <div className={`editor-action ${isOpen && 'is-open'}`}>
            <div
              onClick={(e) => setAnchorElMessage(e.currentTarget)}
              onKeyDown={handleKeyboardEnter((e) =>
                setAnchorElMessage(e?.currentTarget ?? null),
              )}
              role="button"
              tabIndex={0}
              className="label-action"
              aria-describedby={`insert-message-placeholder-${id}`}
            >
              <PopupLink>{popupText}</PopupLink>
            </div>
          </div>
          {isErrored && (
            <div className="error" aria-live="assertive" role="status">
              {errorText}
            </div>
          )}
          <Popover
            id={`insert-message-placeholder-${id}`}
            anchorEl={anchorElMessage}
            onClose={() => setAnchorElMessage(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <PlaceholderMenu
              placeholders={placeholders}
              initialExpand
              onSelect={insertPlaceholder}
              onClose={() => setAnchorElMessage(null)}
            />
          </Popover>
        </>
      ) : (
        <>
          {isErrored && (
            <div className="error" aria-live="assertive" role="status">
              {errorText}
            </div>
          )}
        </>
      )}
    </QuillContainer>
  );
};

export default QuillEditor;
